/* eslint-disable complexity */
import ua from '@tencent/docs-user-agent';
import { Persistent } from '@tencent/slide-sdk-persistent';
import { ErrorSource, Revision, SlideAppError, UserChangeStatus } from '@tencent/slide-sdk-revision';
import { Socket } from '@tencent/slide-sdk-socket';
import {
    SlideFile,
    PresentationEventTypes,
    MutationSource,
    getRandomTaskId,
    RevisionEventTypes,
    PersistentEventTypes,
    OTTransformEventTypes,
    CollaborationEventTypes,
    TransformSource,
    SocketEventTypes,
    CommandEventTypes,
} from '@tencent/slide-sdk-model';
import { assert, type DeepRequired } from 'ts-essentials';
import { FetchSource } from '@tencent/slide-sdk-revision/src/handler/fetch-missing-handler';
import findLastIndex from 'lodash/findLastIndex';
import _get from 'lodash/get';
import { CommandData } from '../app/command-data';
import { TransformTaskManager, TaskType } from './transform-task-manager';
import { CombineUserChange } from './user-change';
import { MonitorStatus, type ILogger } from './logger.interface';
import type { IFollowUserChange } from './user-change';
import type { IOTTransform } from '@tencent/slide-sdk-ot-transform';
import type {
    IMutationsPackExtended,
    IOfflineEditServiceProvider,
    IPersistentConfig,
    SnapshotManager,
} from '@tencent/slide-sdk-persistent';
import type {
    IPresentationDataModel,
    ICommandData,
    ISlideFileData,
    IValidMutationData,
    ITransformCommand,
} from '@tencent/slide-sdk-model';
import type { IContentStatusManager } from './content-status-manager';
import type { IEventManager } from './event-manager';
import type { Mutation } from '@tencent/slide-sdk-model/src/mutation/base/base-mutation';
import type { IConnectionControllerOptions } from '@tencent/docs-connection';
import type { IDownstreamUserChange, IErrorMsg } from '@tencent/slide-sdk-revision/src/types';

interface IClientVars {
    collab_client_vars: IConnectionControllerOptions['serverVars'];
    userId: string;
    subId: string;
    userInfo?: {
        uid: string;
        nowUserIndex: string;
    };
    privilegeAttribute: {
        can_edit: boolean;
    };
    globalPadId: string;
    isDriveFile: boolean;
}

export interface IMutationsPack {
    mutations: IValidMutationData[];
    revision: number;
    taskId: number;
    source: MutationSource;
    canCombine: boolean;
    combineTaskId?: number;
}

export interface ICollaborationManager {
    getSocket(): Socket;
    getRevision(): Revision;
    getPersistent(): Persistent;
    getSnapshot(): SnapshotManager;
    handleNewUserChanges(userChanges: IDownstreamUserChange[]): void;
    addNativeOfflineDataToQueue(persistentMutationsPacks: IMutationsPackExtended[]): void;
    suspendAllChanges(): void;
    resumeAllChanges(): void;
    onCollaborationReady(callback: () => void): void;
}

export enum CollaborationStatus {
    Init = 'init',
    Ready = 'ready',
    Suspend = 'suspend',
}

const MODULE_NAME = '[SDK][CM]';

const SUPPORT_COMBINE = false;

export class CollaborationManager implements ICollaborationManager {
    private status: CollaborationStatus;
    private socket: Socket;
    private revision: Revision;
    private persistent: Persistent;
    private transformTaskManager: TransformTaskManager;
    private snapshotManager: SnapshotManager;
    private taskId = getRandomTaskId();
    private combineTaskId = getRandomTaskId();

    /**
     * 协同推送的编辑数据，不区分来源，包含拉断层和socket推送
     */
    private collaborationUserChanges: IFollowUserChange[] = [];

    /**
     * 本地推送的编辑数据，包含转换过和未转换过的数据
     */
    private followLocalUserChanges: IFollowUserChange[] = [];
    private pollingTimer: ReturnType<typeof setInterval> | null;
    private hasRetried = false;

    /** 转换过程中的临时版本号 */
    private transformRev: number;

    public constructor(
        private presentationProvider: {
            getPresentation(): IPresentationDataModel;
            applyCommandData(command: ICommandData, mutationSource: MutationSource): void;
            getSerializedPresentationData(): ISlideFileData;
        },
        private clientVarsProvider: {
            getClientVars(): IClientVars & any;
            isLocalDoc(): boolean;
        },
        private contentStatusManager: IContentStatusManager,
        private transformManager: IOTTransform,
        private event: IEventManager,
        private logger: ILogger,
        private privilegeProvider: {
            editable: boolean;
        },
        private cacheMutationProvider: {
            getCacheMutations(): Mutation[][];
        },
        private offlineEditServiceProvider?: IOfflineEditServiceProvider,
    ) {
        this.transformTaskManager = new TransformTaskManager(this.transformManager, this.logger);

        this.registerEvent();
        this.init();
    }

    public getSocket() {
        return this.socket;
    }

    public getRevision() {
        return this.revision;
    }

    public getPersistent() {
        return this.persistent;
    }

    public getSnapshot() {
        return this.snapshotManager;
    }

    public setTransformRev = (rev: number) => {
        this.transformRev = rev;
    };

    public getUserChangeFromLocalCache = (taskId: number) => {
        return this.followLocalUserChanges.find(({ taskId: id }) => id === taskId);
    };

    public getUserChangeFromRemoteCache = (taskId: number) => {
        return this.collaborationUserChanges.find(({ taskId: id }) => id === taskId);
    };

    /**
     * 统一处理收到的协同推送数据
     * @param userChanges
     */
    public handleNewUserChanges = (userChanges: IDownstreamUserChange[]) => {
        for (const userChange of userChanges) {
            // 如果推送来的数据版本小于当前转换版本，直接丢弃
            const needRev = this.transformRev + 1;
            if (userChange.revision < needRev) {
                // this.logger.info(MODULE_NAME, 'userChange.revision', userChange.revision, '<', needRev, 'ignore');
                continue;
            }
            const isExist = this.collaborationUserChanges.find(({ taskId }) => taskId === userChange.taskId);
            if (isExist) {
                // this.logger.info(MODULE_NAME, 'userChange already exist, taskId:', userChange.taskId);
                continue;
            }
            this.logger.info(
                MODULE_NAME,
                'add userChange, taskId:',
                userChange.taskId,
                'revision',
                userChange.revision,
            );
            this.collaborationUserChanges.push({
                taskId: userChange.taskId,
                revision: userChange.revision,
                command: new SlideFile({ presentation: userChange.keyframe }).getCommandData(),
                source: MutationSource.Collaborator,
            });
            // 推送不一定是有序的，所以需要重新排序
            this.collaborationUserChanges.sort(
                (userChangeA, userChangeB) => userChangeA.revision - userChangeB.revision,
            );
        }
    };

    /**
     * Native 打开文档时，将未发送的离线数据添加到待发送队列
     * @param persistentMutationsPacks
     */
    public addNativeOfflineDataToQueue = (persistentMutationsPacks: IMutationsPackExtended[]) => {
        const mutationsPacks = persistentMutationsPacks.map(({ mutations, revision, taskId, canCombine }) => ({
            mutations,
            revision,
            taskId,
            source: MutationSource.Offline,
            canCombine,
        }));
        this.logger.info(MODULE_NAME, 'addNativeOfflineDataToQueue', mutationsPacks.length);
        this.addMutationPacksToFollowLocalUserChanges(mutationsPacks);
    };

    /** 暂停 所有 (上行/下行) 变更 (apply/submit/冲突处理) */
    public suspendAllChanges = (): void => {
        this.stopPollingTimer();
        this.changeCollaborationStatus(CollaborationStatus.Suspend);
    };

    /** 恢复 所有 (上行/下行) 变更 (apply/submit/冲突处理) */
    public resumeAllChanges = (): void => {
        this.startPeriodicPolling();
        this.changeCollaborationStatus(CollaborationStatus.Ready);
    };

    public onCollaborationReady = (callback: () => void) => {
        if (this.status === CollaborationStatus.Ready) {
            callback();
            return;
        }
        const unRegisterEvent = this.event.on(CollaborationEventTypes.StatusChange, (status: CollaborationStatus) => {
            if (status === CollaborationStatus.Ready) {
                unRegisterEvent();
                callback();
                return;
            }
        });
    };

    private changeCollaborationStatus = (status: CollaborationStatus) => {
        this.status = status;
        this.event.emit(CollaborationEventTypes.StatusChange, status);
    };

    private applyCommandData = (command: ICommandData, mutationSource: MutationSource) => {
        try {
            this.presentationProvider.applyCommandData(command, mutationSource);
            return true;
        } catch (err: any) {
            this.handleAndDispatchFatalError(mutationSource === MutationSource.Offline);
            return false;
        }
    };

    /**
     * 前端主动抛错，使用 60003 错误码
     * @param errorCode
     */
    private handleFatalError = (errorCode = 60003) => {
        this.stopPollingTimer();
        this.socket.close('handleFatalError', errorCode);
    };

    /**
     * 处理严重错误时，将当前的 mutation 来源上抛
     * @param source
     */
    private handleAndDispatchFatalError = (isOffline = false) => {
        this.handleFatalError(SlideAppError.None);
        this.event.emit(CollaborationEventTypes.FatalError, isOffline);
    };

    private stopPollingTimer() {
        if (this.pollingTimer !== null) {
            clearInterval(this.pollingTimer);
            this.pollingTimer = null;
        }
    }

    /**
     * 开启本地发送、冲突处理的定时器
     * @returns
     */
    private startPeriodicPolling() {
        if (this.pollingTimer) return;
        this.pollingTimer = setInterval(() => {
            if (!this.pollingTimer) return;
            this.checkAndApplyCommands();
        }, 16);
    }

    /**
     * 提交基准本地数据
     * @returns
     */
    private submitBenchMarkCommand() {
        const [userChange] = this.followLocalUserChanges;
        const submittingTaskId = this.revision.getSubmittingUserChange()?.taskId;
        // 如果正在发送的任务id和本地第一个任务id相同，说明正在发送，不需要重复发送
        if (
            !this.socket.opened ||
            (submittingTaskId &&
                (submittingTaskId === userChange.taskId || submittingTaskId === userChange.combineTaskId))
        ) {
            return;
        }
        // 如果是空包不发送
        if (!userChange.command.mutations?.length) {
            const userChange = this.followLocalUserChanges.shift()!;
            this.persistent.deletionSingle(userChange.taskId);
            return;
        }
        if (!userChange.canCombine || this.followLocalUserChanges.length === 1) {
            this.revision.submitCommand([userChange.command], userChange.taskId);
            return;
        }
        const combineUserChange = this.createCombineUserChange();
        this.logger.info(
            MODULE_NAME,
            'submit local userChange, combineTaskId:',
            combineUserChange.getTaskId(),
            'taskId:',
            combineUserChange.getRawTaskId(),
            'revision',
            combineUserChange.getRevision(),
            combineUserChange.getRawUserChanges(),
            'size',
            combineUserChange.getSize() / 1024,
            'KB',
        );
        this.persistent.replaceAllData(
            this.followLocalUserChanges.map(({ taskId, command, source, canCombine, revision }) =>
                this.createMutationsPack(
                    CommandData.fromMutationData(command.mutations ?? []).getValidMutationData(),
                    revision,
                    taskId,
                    source,
                    canCombine,
                    combineUserChange.getTaskId(),
                ),
            ),
        );
        this.revision.submitCommand(
            combineUserChange.getRawUserChanges().map(({ command }) => command),
            combineUserChange.getTaskId(),
        );
    }

    private createCombineUserChange() {
        const combineUserChange = new CombineUserChange();
        combineUserChange.add(this.followLocalUserChanges.slice());
        combineUserChange.setTaskId(this.getNextCombineTaskId());
        return combineUserChange;
    }

    /**
     * 本地数据发送、冲突处理、协同数据应用的核心逻辑
     * @returns
     */
    private checkAndApplyCommands() {
        /**
         * 以下几种情况不响应处理
         * 1. 正在转换
         * 2. 没有协同数据，没有本地数据
         */
        if (
            this.transformTaskManager.isTransforming ||
            (!this.collaborationUserChanges.length && !this.followLocalUserChanges.length)
        )
            return;
        // 如果没有协同数据，只有本地数据，尝试去提交
        if (!this.collaborationUserChanges.length && this.followLocalUserChanges.length) {
            this.handleAbnormalSubmittingUserChange();
            this.submitBenchMarkCommand();
            return;
        }

        assert(this.collaborationUserChanges.length, 'collaborationUserChanges.length should not be 0');
        const [collaborationUserChange] = this.collaborationUserChanges;

        // 当前依赖的转换版本号
        const curNeedTransformRev = this.transformRev + 1;

        // 如果缺少转换版本的 userChange，尝试去拉取
        if (collaborationUserChange?.revision !== curNeedTransformRev) {
            if (this.hasRetried) {
                return;
            }
            this.hasRetried = true;
            // 如果去重后仍有版本号低于当前版本号的数据，直接丢弃
            if (collaborationUserChange?.revision < curNeedTransformRev) {
                const collaborationUserChange = this.collaborationUserChanges.shift();
                this.logger.info(
                    MODULE_NAME,
                    'collaborationUserChange.revision',
                    collaborationUserChange?.revision,
                    '<',
                    curNeedTransformRev,
                    ', drop it',
                );
            }
            this.logger.warn(MODULE_NAME, 'need ', curNeedTransformRev, ' try to fetch it');
            this.revision
                .getMissingChanges(curNeedTransformRev, curNeedTransformRev, FetchSource.Transform)
                .then((userChanges) => {
                    this.hasRetried = false;
                    this.handleNewUserChanges(userChanges);
                });
            return;
        }

        // 有协同数据，没有本地数据，直接应用本地数据
        if (!this.followLocalUserChanges.length) {
            const { command, taskId, revision } = collaborationUserChange;
            this.logger.info(
                MODULE_NAME,
                'no conflict, apply collaboration',
                taskId,
                'revision:',
                revision,
                command.mutations,
            );
            if (command.mutations?.length) {
                const applyRes = this.applyCommandData(command, MutationSource.Collaborator);
                if (!applyRes) return;
            }
            this.collaborationUserChanges.shift();
            this.revision.updateRevisionNumber(revision);
            return;
        }

        // 获取当前基准 command 的 taskId，有可能存在基准还没来得及发出去的情况
        const benchMarkTaskId = (() => {
            if (!this.revision.getSubmittingUserChange()?.taskId) {
                this.submitBenchMarkCommand();
            }
            return this.revision.getSubmittingUserChange()?.taskId;
        })();
        this.logger.info(
            MODULE_NAME,
            'benchMarkTaskId is:',
            benchMarkTaskId,
            'cur collaboration taskId is',
            collaborationUserChange.taskId,
        );
        // 是否是基准 command
        const isBenchmarkCommand =
            benchMarkTaskId &&
            (collaborationUserChange.taskId === benchMarkTaskId ||
                collaborationUserChange.combineTaskId === benchMarkTaskId);
        if (isBenchmarkCommand) {
            this.collaborationUserChanges.shift();
            this.handleBenchmarkCommand(benchMarkTaskId, collaborationUserChange.revision);
            return;
        }
        this.handleMergeConflict(collaborationUserChange);
    }

    private handleBenchmarkCommand(benchMarkTaskId: number, benchmarkRev: number) {
        const lastIndex = findLastIndex(
            this.followLocalUserChanges,
            (userChange) => userChange.taskId === benchMarkTaskId || userChange.combineTaskId === benchMarkTaskId,
        );
        if (lastIndex < 0) return;
        // 移除内存旧的基准
        this.followLocalUserChanges.splice(0, lastIndex + 1);
        this.acceptCommit(benchMarkTaskId, benchmarkRev);
    }

    /**
     * 接受基准数据
     * @param benchMarkTaskId
     * @param benchmarkRev
     */
    private acceptCommit(benchMarkTaskId: number, benchmarkRev: number) {
        // DB中所有未发送的缓存，更新为转换后的内容+新版本号
        this.persistent.replaceAllData(
            this.followLocalUserChanges.map(({ taskId, command, source, canCombine, combineTaskId }) =>
                this.createMutationsPack(
                    CommandData.fromMutationData(command.mutations ?? []).getValidMutationData(),
                    benchmarkRev,
                    taskId,
                    source,
                    canCombine,
                    combineTaskId,
                ),
            ),
        );
        // 更新待发送队列中的版本号
        this.followLocalUserChanges.forEach((localUserChange) => {
            localUserChange.revision = benchmarkRev;
        });
        this.revision.updateRevisionNumber(benchmarkRev);
        this.logger.info(
            MODULE_NAME,
            'acceptCommit, taskId:',
            benchMarkTaskId,
            'benchmarkRev',
            benchmarkRev,
            ',wait to send count',
            this.followLocalUserChanges.length,
        );
        this.revision.resetSubmittingUserChange();
    }

    private async handleMergeConflict(collaborationUserChange: IFollowUserChange) {
        const localCommands = this.followLocalUserChanges.map(({ command, taskId }) =>
            this.commandToTransformCommand(command, taskId),
        );
        const collaborationCommands = [
            this.commandToTransformCommand(collaborationUserChange.command, collaborationUserChange.taskId),
        ];

        this.transformTaskManager.addTask(collaborationCommands, TaskType.Collaboration);
        this.transformTaskManager.addTask(localCommands, TaskType.Local);

        this.logger.info(MODULE_NAME, 'start transform');
        let followCollaborationCommands: ICommandData[] = [];
        let followLocalCommands: ICommandData[] = [];
        try {
            const res = await this.transformTaskManager.startTransform();
            followCollaborationCommands = res.followCollaborationCommands.map(this.transformCommandToCommand);
            followLocalCommands = res.followLocalCommands.map(this.transformCommandToCommand);
        } catch {
            this.handleAndDispatchFatalError();
            return;
        }
        this.logger.info(MODULE_NAME, 'end transform');

        this.logger.info(
            MODULE_NAME,
            'updated followLocalUserChanges:',
            this.followLocalUserChanges,
            followLocalCommands,
        );
        // 手动更新转换后的本地数据
        followLocalCommands.forEach((followLocalCommand, followIndex) => {
            this.followLocalUserChanges[followIndex].command = followLocalCommand;
        });

        for (const command of followCollaborationCommands) {
            if (!command.mutations?.length) continue;
            const applyRes = this.applyCommandData(command, MutationSource.CollaboratorConflict);
            if (!applyRes) return;
        }

        this.collaborationUserChanges.shift();
        this.setTransformRev(this.transformRev + 1);
        this.handleAbnormalSubmittingUserChange();
    }

    private init() {
        this.changeCollaborationStatus(CollaborationStatus.Init);
        const { globalPadId, userId } = this.clientVarsProvider.getClientVars();
        this.logger.info(MODULE_NAME, '[init]', `globalPadId:${globalPadId}, userId:${userId}`);

        this.initSocket();
        this.initRevision();
        this.initPersistent();
        // 初始化版本号
        this.setTransformRev(this.revision.getRevisionNumber());
        this.onCollaborationReady(() => {
            this.startPeriodicPolling();
            this.readyToOpenSocket();
        });
        this.addCacheMutation();
    }

    /**
     * 提交 Collaboration 初始化前，用户产生的编辑行为
     */
    private addCacheMutation() {
        this.cacheMutationProvider.getCacheMutations().forEach((mutations) => {
            this.submitMutations({ mutations, source: MutationSource.Editor });
        });
    }

    private addPresentationEventListeners() {
        this.event.on(PresentationEventTypes.SubmitMutations, this.submitMutations);

        // 延迟初始化，等收到协同消息再初始化同步的wasm
        const unRegisterInit = this.event.on(PresentationEventTypes.CommitCommand, (_, __, source: MutationSource) => {
            if ([MutationSource.Collaborator, MutationSource.CollaboratorConflict].includes(source)) {
                this.transformManager.initSyncTransformer();
                unRegisterInit();
            }
        });
    }

    private addRevisionEventListeners() {
        this.event.on(RevisionEventTypes.CommitFail, this.handleRevisionCommitFailed);
        this.event.on(RevisionEventTypes.CommitSucceed, this.handleRevisionCommitSucceed);
        this.event.on(RevisionEventTypes.UpdateBaseRevision, this.setTransformRev);
        this.event.on(RevisionEventTypes.FetchMissingDataError, () => this.handleFatalError(SlideAppError.None));
    }

    private addPersistentEventListeners() {
        this.event.on(PersistentEventTypes.GetAllMutationsPacks, this.handlePersistentGetAllMutationsPacks);
    }

    private addSocketEventListeners() {
        this.event.on(SocketEventTypes.RevertMessage, () => {
            this.logger.error(MODULE_NAME, 'shutdown by revert doc');
            this.handleFatalError(SlideAppError.None);
        });
    }

    private addOTTransformEventListeners() {
        this.event.on(OTTransformEventTypes.TransformError, (_, msg) => {
            this.logger.error(MODULE_NAME, 'shutdown by otTransform error:', msg);
            this.handleFatalError(SlideAppError.None);
        });
    }

    private addCommandEventListeners() {
        this.event.on(CommandEventTypes.Error, () => this.handleAndDispatchFatalError());
    }

    private registerEvent() {
        this.addPresentationEventListeners();
        this.addRevisionEventListeners();
        this.addPersistentEventListeners();
        this.addSocketEventListeners();
        this.addOTTransformEventListeners();
        this.addCommandEventListeners();
    }

    private initSocket() {
        if (!this.socket) {
            const clientVars = this.clientVarsProvider.getClientVars();
            this.socket = new Socket(clientVars, {}, this.event, this.logger);
            const { collab_client_vars: serverVars, userId } = clientVars;
            this.socket.init({
                serverVars,
                userId,
                uid: this.getUid(),
                nowUserIndex: this.clientVarsProvider.getClientVars().userInfo?.nowUserIndex,
                headerGeneralPacket: {
                    // rel_rev: globalRavenReleaseVersion,
                    right_tag: Number(!!this.privilegeProvider.editable),
                    platform: this.getPlatformType(),
                },
            });
        }
    }

    private initRevision() {
        if (!this.revision) {
            this.revision = new Revision(this, this.socket, this.event, this.logger);
            const { userId, subId, collab_client_vars: serverVars } = this.clientVarsProvider.getClientVars() ?? {};
            this.revision.init({
                uuId: userId,
                subId,
                oSubId: true,
                baseRev: serverVars?.rev,
                globalPadId: serverVars?.globalPadId,
                xsrf: this.getCookie('TOK') ?? '',
            });
        }
    }

    private initPersistent() {
        if (!this.persistent) {
            this.persistent = new Persistent(this.event, this.logger, this.offlineEditServiceProvider);
            const { globalPadId, localPadId, userId } = this.clientVarsProvider.getClientVars();
            const persistentConfig: IPersistentConfig = {
                globalPadId,
                localPadId,
                userId,
                editable: this.privilegeProvider.editable,
                revision: this.revision.getRevisionNumber(),
            };
            const initPromise = this.persistent.init(persistentConfig).catch((err) => {
                this.logger.error(MODULE_NAME, 'uncaught persistent init error', err.toString());
            });

            initPromise
                .then((hasOfflineData) => {
                    return new Promise<void>((resolve) => {
                        // 文档加载完成后，初始化持久化模块
                        this.contentStatusManager.onDocumentReady(() => {
                            if (!hasOfflineData) return resolve();

                            // 等到数据恢复完成后再开启 socket
                            this.persistent.startToRecovery().then(resolve);
                        });
                    });
                })
                .finally(() => {
                    this.changeCollaborationStatus(CollaborationStatus.Ready);
                });
        }
    }

    private handleRevisionCommitSucceed = async (userChange: { taskId: number; baseRev: number; newRev: number }) => {
        const { taskId, baseRev, newRev } = userChange ?? {};
        if (!taskId || newRev === undefined || baseRev === undefined) return;

        // 上报成功日志
        const localChange = this.followLocalUserChanges.find(({ taskId }) => taskId === userChange?.taskId);
        localChange && this.reportCommitStatus(MonitorStatus.SUCCESS, localChange.source);
        this.logger.info(MODULE_NAME, 'submit success, taskId:', taskId, 'newRev:', newRev);

        /**
         * 此时收到回包，可能是几种情况：
         * 1. 立刻收到回包，版本号差1
         * 2. 立刻收到回包，版本号差1以上
         * 3. 协同推过来了一部分，收到了回包
         * 4. 协同推过来了所有协同，就差回包数据
         * 5. 推送太慢，checkAndApply通过兜底拉齐的方式拉到了回包，收到了回包
         */

        // scene5
        if (this.transformRev >= newRev) {
            this.logger.info(MODULE_NAME, `response ${userChange?.taskId} too slow, already accept it, ignore`);
            return;
        }

        // scene1
        if (baseRev + 1 === newRev) {
            this.handleBenchmarkCommand(taskId, newRev);
            return;
        }

        // scene2/3/4
        const missingChanges = await this.revision.getMissingChanges(
            this.transformRev + 1,
            newRev,
            FetchSource.CommitSuccess,
        );
        this.handleNewUserChanges(missingChanges);
    };

    private handleRevisionCommitFailed = ({ taskId, code, message, source, error }: DeepRequired<IErrorMsg>) => {
        if (source === ErrorSource.Room || error === SlideAppError.None) return;
        // if (
        //     [
        //         ServerErrorCode.COMMIT_ERROR_FOLLOW_FAILED,
        //         ServerErrorCode.COMMIT_ERROR_CS_LIMIT_ERROR,
        //         ServerErrorCode.CGO_TRANSFORM,
        //         ServerErrorCode.OVERSIZE_COMMIT,
        //     ].includes(code)
        // ) {
        //     // 冲突处理失败、cs体积太大，通知持久化模块进行删除操作
        //     this.persistent.error(taskId);
        // } else {
        //     this.persistent.modify(taskId, code);
        // }
        // 如果是合包提交失败，拆包并且重试
        const isCombineTaskId = !!this.followLocalUserChanges.find(
            ({ combineTaskId, canCombine }) => canCombine && combineTaskId === taskId,
        );
        if (isCombineTaskId) {
            this.logger.warn(MODULE_NAME, 'combineTaskId:', taskId, 'commit error, split it and reSend');
            this.followLocalUserChanges.forEach((userChange) => {
                if (userChange.combineTaskId === taskId) {
                    userChange.canCombine = false;
                    userChange.combineTaskId = undefined;
                }
            });
            this.persistent.replaceAllData(
                this.followLocalUserChanges.map(({ taskId, command, source, canCombine, revision }) =>
                    this.createMutationsPack(
                        CommandData.fromMutationData(command.mutations ?? []).getValidMutationData(),
                        revision,
                        taskId,
                        source,
                        canCombine,
                    ),
                ),
            );
            this.revision.resetSubmittingUserChange();
            return;
        }
        // 错误码不直接删除DB，记录重试次数，重试次数超过阈值走SnapShot面板删除
        this.persistent.modifyErrorInfo(taskId, code);
        const localChange = this.followLocalUserChanges.find(({ taskId }) => taskId === taskId);
        localChange && this.reportCommitStatus(MonitorStatus.ERROR, localChange.source, message, code);
        this.logger.warn(MODULE_NAME, `Send failed, taskId:${taskId}, code:${code}`);
    };

    /**
     * 从 DB 里加载所有的离线变更数据
     * @param persistentMutationsPacks
     */
    private handlePersistentGetAllMutationsPacks = async (
        {
            mutationsPacks: persistentMutationsPacks,
        }: {
            mutationsPacks: IMutationsPackExtended[];
        },
        done: () => void,
    ) => {
        const mutationsPacks = [...persistentMutationsPacks];
        if (mutationsPacks[0].sendedCount > 3) {
            this.logger.warn(MODULE_NAME, 'offline data retried exceed max limit, need drop it');
            this.handleAndDispatchFatalError(true);
            return;
        }
        const version = mutationsPacks[0].revision;
        const needFetchMissingChanges = version < this.revision.getRevisionNumber();
        this.logger.info(
            MODULE_NAME,
            'offline version is',
            version,
            'latest version is',
            this.revision.getRevisionNumber(),
        );
        // 如果离线数据和server有版本差，拉取差异数据。从基准的下一个版本开始拉取，拉取到server最新版本
        const missingChanges: IDownstreamUserChange[] = needFetchMissingChanges
            ? await this.revision.getMissingChanges(version + 1, this.revision.getRevisionNumber(), FetchSource.Offline)
            : [];
        if (mutationsPacks?.length) {
            const validMissingChanges = missingChanges.filter(({ revision }) => revision >= version);
            if (validMissingChanges.length) {
                await this.transformAndApplyMutationPacks(mutationsPacks, validMissingChanges);
            } else {
                await this.applyMutationPacks(mutationsPacks);
            }
        }
        done();
    };

    /**
     * 应用离线数据
     * @param mutationsPacks
     */
    private applyMutationPacks(mutationsPacks: IMutationsPackExtended[]) {
        this.logger.info(`${MODULE_NAME} no need transform offline mutationsPacks, length ${mutationsPacks.length}`);
        for (const { mutations: iValidMutationsData } of mutationsPacks) {
            const commandData = CommandData.fromValidMutationData(iValidMutationsData).getCommandData();
            const applyRes = this.applyCommandData(commandData, MutationSource.Offline);
            if (!applyRes) return;
        }
        return this.submitOfflineMutations(mutationsPacks);
    }

    /**
     * 离线数据与断层数据转换后应用
     * @param mutationsPacks
     * @param missingChanges
     * @returns
     */
    private async transformAndApplyMutationPacks(
        mutationsPacks: IMutationsPackExtended[],
        missingChanges: IDownstreamUserChange[],
    ) {
        const mutationCommandPacks: Array<
            Omit<IMutationsPackExtended, 'mutations' | 'sendedCount'> & { command: ICommandData }
        > = mutationsPacks.map(({ taskId, source, revision, mutations, canCombine }) => ({
            taskId,
            source,
            revision,
            command: CommandData.fromValidMutationData(mutations).getCommandData(),
            canCombine,
        }));
        this.logger.info(
            MODULE_NAME,
            mutationCommandPacks[0].revision,
            'transform to',
            missingChanges[missingChanges.length - 1].revision,
            mutationCommandPacks.map(({ command }) => command),
            missingChanges,
        );
        for (let i = 0; i < missingChanges.length; i++) {
            const missingChange = missingChanges[i];
            // 如果离线数据里没有未提交的离线数据，直接跳出循环
            if (!mutationCommandPacks.length) break;
            // 尝试去找断层数据里是否有未提交的离线数据，如果有则需要剔除并更新 DB
            if (missingChange.taskId === mutationCommandPacks[0].taskId) {
                mutationCommandPacks.shift();
                // 更新转换后的离线数据+新版本号
                this.persistent.replaceAllData(
                    mutationCommandPacks.map(({ taskId, source, command, canCombine }) => ({
                        taskId,
                        source,
                        mutations: CommandData.fromMutationData(command.mutations ?? []).getValidMutationData(),
                        revision: missingChange.revision,
                        canCombine,
                    })),
                );
                // 离线去重，补一次成功上报
                this.reportCommitStatus(MonitorStatus.SUCCESS, MutationSource.Editor);
                continue;
            }

            try {
                const missingMutations = new SlideFile({ presentation: missingChange.keyframe }).getCommandData()
                    .mutations;
                const missingCommand = { mutations: missingMutations };
                let followOfflineCommands: ICommandData[] = mutationCommandPacks.map(({ command }) => command);
                this.logger.info(MODULE_NAME, 'transform offlineData', followOfflineCommands, missingCommand);
                const result = await this.transformManager.transform(
                    followOfflineCommands,
                    [missingCommand],
                    true,
                    TransformSource.Offline,
                );
                followOfflineCommands = result.transformedA;
                mutationCommandPacks.forEach((pack, index) => (pack.command = followOfflineCommands[index]));
            } catch (e: any) {
                this.logger.error(MODULE_NAME, 'transform offlineData error:', e.toString());
                this.handleAndDispatchFatalError(true);
                throw e;
            }
        }

        this.logger.info(
            MODULE_NAME,
            'transform offlineData success',
            mutationCommandPacks.map(({ command }) => command),
        );
        const removedTaskIds: number[] = [];
        for (const pack of mutationCommandPacks) {
            const iMutationsData = pack.command.mutations;
            if (!iMutationsData?.length) {
                removedTaskIds.push(pack.taskId);
                continue;
            }
            const applyRes = this.applyCommandData({ mutations: iMutationsData }, MutationSource.Offline);
            if (!applyRes) return;
        }
        this.persistent.deletionMultiple(removedTaskIds);
        return this.submitOfflineMutations(
            mutationCommandPacks
                .filter((pack) => !removedTaskIds.includes(pack.taskId))
                .map(({ taskId, revision, command, canCombine }) => ({
                    taskId,
                    revision,
                    mutations: CommandData.fromMutationData(command.mutations ?? []).getValidMutationData(),
                    source: MutationSource.Offline,
                    canCombine,
                })),
        );
    }

    private submitMutations = ({ mutations, source }: { mutations: Mutation[]; source: MutationSource }) => {
        if (!mutations.length) return;
        this.taskId += 1;
        const mutationsPack = this.createMutationsPack(
            mutations.map((mut) => mut.toData()),
            this.revision.getRevisionNumber(),
            this.taskId,
            source,
        );
        this.persistent.additionOrUpdate(mutationsPack);
        this.logger.info(
            MODULE_NAME,
            'new localChange, taskId',
            mutationsPack.taskId,
            'revision',
            mutationsPack.revision,
            'source',
            `"${mutationsPack.source}"`,
            mutationsPack.mutations,
        );
        this.addMutationPacksToFollowLocalUserChanges([mutationsPack]);

        // 如果正在进行转换，直接添加到转换队列中。当前新产生的本地编辑是基于转换前的版本产生的，协同数据必须经过转换才能应用
        if (this.transformTaskManager.isTransforming) {
            const commandData = CommandData.fromValidMutationData(mutationsPack.mutations).getCommandData();
            this.transformTaskManager.addTask(
                [this.commandToTransformCommand(commandData, mutationsPack.taskId)],
                TaskType.Local,
            );
        }
    };

    private async submitOfflineMutations(mutationPacks: IMutationsPack[]) {
        if (!mutationPacks.length) return;
        mutationPacks.forEach((pack) => {
            pack.revision = this.revision.getRevisionNumber();
            pack.source = MutationSource.Offline;
        });
        const res = await this.persistent.replaceAllData(mutationPacks);
        if (!res) return;
        mutationPacks.forEach((pack) => {
            this.logger.info(
                MODULE_NAME,
                'new localChange, taskId',
                pack.taskId,
                'revision',
                pack.revision,
                'source',
                pack.source,
                pack.mutations,
            );
            this.addMutationPacksToFollowLocalUserChanges([pack]);

            // 如果正在进行转换，直接添加到转换队列中。当前新产生的本地编辑是基于转换前的版本产生的，协同数据必须经过转换才能应用
            if (this.transformTaskManager.isTransforming) {
                const commandData = CommandData.fromValidMutationData(pack.mutations).getCommandData();
                this.transformTaskManager.addTask(
                    [this.commandToTransformCommand(commandData, pack.taskId)],
                    TaskType.Local,
                );
            }
        });
    }

    private createMutationsPack(
        IValidMutationDatas: IValidMutationData[],
        revision: number,
        taskId: number,
        source: MutationSource,
        canCombine = SUPPORT_COMBINE,
        combineTaskId?: number,
    ): IMutationsPack {
        return {
            mutations: IValidMutationDatas,
            revision,
            taskId,
            source,
            canCombine,
            combineTaskId,
        };
    }

    private addMutationPacksToFollowLocalUserChanges(mutationsPacks: IMutationsPack[]) {
        const localNewUserChanges = mutationsPacks.map(({ mutations, taskId, revision, source, canCombine }) => ({
            command: CommandData.fromValidMutationData(mutations).getCommandData(),
            taskId,
            revision,
            source,
            canCombine,
        }));
        this.followLocalUserChanges.push(...localNewUserChanges);
    }

    private getPlatformType() {
        if (ua.isWindows) return 1;
        if (ua.isAndroid) return 2;
        if (ua.isIOS) return 3;
        if (ua.isMac) return 5;
        return 4;
    }

    private getCookie(key: string) {
        const r = new RegExp(`(?:^|;+|\\s+)${key}=([^;]*)`);
        const m = document.cookie.match(r);
        return (!m ? '' : m[1]) || null;
    }

    private reportCommitStatus(status: MonitorStatus, source: MutationSource, errMsg?: string, errCode?: number) {
        const key = '关键请求失败';
        this.logger.monitor?.reportStatus?.(key, status, errMsg, [
            source,
            errCode?.toString() ?? '',
            'ignore',
            this.clientVarsProvider.getClientVars().globalPadId ?? '',
        ]);
        if (status === MonitorStatus.ERROR) {
            this.logger?.getPrototype?.().report('SEND_REPORT', {
                query: {
                    startTs: Date.now() - 24 * 60 * 60 * 1000,
                    endTs: Date.now(),
                },
            });
        }
    }

    private getUid() {
        const global = window as any;
        const getUidFromBasicClientVars = () => global.basicClientVars?.userInfo?.uid;
        const getUidFromCookie = () =>
            global.AppConfig?.normal?.npm['@tencent/docs-login']?.USE_TDOCS_UID
                ? this.getCookie('TDOCS_UID')
                : this.getCookie('uid');
        const getUidFromGlobalMultiUser = () => global.global_multi_user?.uid;
        const getUidFromClientVars = () => {
            const { tinyId, uid } = this.clientVarsProvider.getClientVars()?.userInfo || {};
            return tinyId || uid;
        };
        return (
            getUidFromBasicClientVars() || getUidFromCookie() || getUidFromGlobalMultiUser() || getUidFromClientVars()
        );
    }

    private commandToTransformCommand(commandData: ICommandData, taskId: number): ITransformCommand {
        return {
            seq: taskId.toString(),
            mutations: commandData.mutations,
        };
    }

    private transformCommandToCommand(transformCommand: ITransformCommand): ICommandData {
        return {
            mutations: transformCommand.mutations,
        };
    }

    private getNextCombineTaskId() {
        this.combineTaskId += 1;
        return this.combineTaskId;
    }

    /**
     * 每次更新转换版本号后/发送前，需要判断正在发送的 userChange 是否处于异常的状态
     * @returns
     */
    private handleAbnormalSubmittingUserChange() {
        const submittingUserChange = this.revision.getSubmittingUserChange();
        const status = submittingUserChange?.getStatus();
        const expectedRev = submittingUserChange?.getExpectedRev() ?? -1;
        if (!submittingUserChange || status !== UserChangeStatus.BaseRevTooLow || this.transformRev < expectedRev)
            return;
        this.logger.info(
            MODULE_NAME,
            'taskId',
            submittingUserChange.taskId,
            'has solved the problem of too low version',
            'from',
            submittingUserChange.revision,
            'upgrade to',
            this.transformRev,
        );
        // DB中所有未发送的缓存，更新为转换后的内容+新版本号
        this.persistent.replaceAllData(
            this.followLocalUserChanges.map(({ taskId, command, source, canCombine, combineTaskId }) =>
                this.createMutationsPack(
                    CommandData.fromMutationData(command.mutations ?? []).getValidMutationData(),
                    this.transformRev,
                    taskId,
                    source,
                    canCombine,
                    combineTaskId,
                ),
            ),
        );
        // 更新待发送队列中的版本号
        this.followLocalUserChanges.forEach((localUserChange) => {
            localUserChange.revision = this.transformRev;
        });
        this.revision.updateRevisionNumber(this.transformRev);
        this.revision.resetSubmittingUserChange();
    }

    private readyToOpenSocket() {
        if (this.clientVarsProvider.isLocalDoc()) return;
        this.socket.open();
    }
}
